import React, { useState, useEffect } from "react";
import axios from "axios"
import { useLocation, useNavigate } from "react-router-dom";
import { useAnalytics } from "../../hooks/useAnalytics";

import {
    Container,
    Typography,
    Button,
    Stepper,
    Step,
    StepLabel,
    Box,
    TextField,
    FormControl,
    FormControlLabel,
    useMediaQuery,
    Select,
    MenuItem,
    Checkbox,
    FormHelperText
} from "@mui/material";
import SuccessModal from "../../components/successModal/SuccessModal";
import "./KycForm.css"

const steps = [
    "Personal Information",
    "Business Information",
    "Contact Details",
    "Business Account Details",
    "Authorized Representatives",
    "Acknowledgement & Acceptance"
];

const idOptions = [
    { key: "Registered", label: "Registered" },
    { key: "Unregistered", label: "Unregistered" },
];

const KycForm = () => {
    useAnalytics();
    
    const location = useLocation();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const isSmallScreen = useMediaQuery("(max-width:600px)");

    const [open, setOpen] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false)

    const [errors, setErrors] = useState({});

    const handleBack = () => setActiveStep((prevStep) => prevStep - 1);

    const handleNext = () => {
        if (validateStep()) {
            setActiveStep((prevStep) => prevStep + 1);
        }
    };

    // Function to get query parameters
    const getQueryParams = () => {
        const searchParams = new URLSearchParams(location.search);
        return {
            firstName: searchParams.get("firstName") || "",
            lastName: searchParams.get("lastName") || "",
            email: searchParams.get("email") || "",
        };
    };

    useEffect(() => {
        const params = getQueryParams();
        // console.log({ params })

        setFormData((prev) => ({
            ...prev,
            personalInfo: {
                ...prev.personalInfo,
                firstName: params.firstName,
                otherNames: params.lastName,
                email: params.email,
            },
            contactDetails: {
                ...prev.contactDetails,

                contactEmail: params.email,
            }
        }));
    }, [location.search]);

    const [formData, setFormData] = useState({
        personalInfo: {
            firstName: "",
            otherNames: "",
            residentialAddress: "",
            stateOfOrigin: "",
            idType: "",
            idNumber: ""
        },
        businessInfo: {
            entityType: "",
            entityName: "",
            tradeName: "",
            registrationYear: "",
            rcNumber: "",
            businessAddress: ""
        },
        contactDetails: {
            contactEmail: "",
            businessEmail: "",
            mobileNumber: "",
            whatsappNumber: "",
            socialMediaHandles: ""
        },
        accountDetails: {
            bankName: "",
            accountName: "",
            accountNumber: ""
        },
        authorizedRepresentatives: {
            rep1: { fullName: "", email: "", mobileNumber: "" },
            rep2: { fullName: "", email: "", mobileNumber: "" }
        },
        acknowledgment: {
            entityName: "",
            authorizedRep: "",
            date: Date.now,
            confirmation: ""
        }
    });



    const handleChange = (e, section, field, subField = null) => {
        const { value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: subField
                    ? { ...prev[section][field], [subField]: value }
                    : value
            }
        }));

        // Clear errors when user types
        setErrors((prev) => ({
            ...prev,
            [`${section}.${field}`]: ""
        }));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true)
        if (validateStep()) {
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_API}/kyc-form`,
                    formData,
                    {
                        headers: { "Content-Type": "application/json" }
                    }
                );

                if (response.status === 201) {
                    // console.log("Form data saved successfully:", response.data.data);
                    setIsSubmitting(false)
                    setOpen(true);
                } else {
                    setIsSubmitting(false)
                    console.error("Error saving form data:");
                }
            } catch (error) {
                setIsSubmitting(false)
                console.error("Network error:", error);
            }
        }

    }

    const renderStepContent = (step) => {
        return (
            <Box sx={{ maxHeight: "70vh", overflowY: "auto", padding: 2 }}>
                {step === 0 && (
                    <>
                        <TextField label="First Name" fullWidth margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.personalInfo.firstName}
                            onChange={(e) => handleChange(e, "personalInfo", "firstName")}
                            error={!!errors["personalInfo.firstName"]}
                            helperText={errors["personalInfo.firstName"]}
                            disabled={formData.personalInfo.firstName.length > 1}
                        />

                        <TextField label="Last Name" fullWidth margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.personalInfo.otherNames}
                            onChange={(e) => handleChange(e, "personalInfo", "otherNames")}
                            error={!!errors["personalInfo.otherNames"]}
                            helperText={errors["personalInfo.otherNames"]}
                            disabled={formData.personalInfo.otherNames.length > 1}
                        />
                        <TextField label="Residential Address" fullWidth multiline rows={2} margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.personalInfo.residentialAddress}
                            onChange={(e) => handleChange(e, "personalInfo", "residentialAddress")}
                            error={!!errors["personalInfo.residentialAddress"]}
                            helperText={errors["personalInfo.residentialAddress"]}
                        />

                        {/* <FormControl fullWidth margin="normal" required error={!!errors["personalInfo.idType"]}>
                            <Select
                                value={formData.personalInfo.idType || ""}
                                onChange={(e) => handleChange(e, "personalInfo", "idType")}
                                displayEmpty
                                renderValue={(selected) =>
                                    selected ? idOptions.find((option) => option.key === selected)?.label : <span style={{ color: "gray" }}>ID Type</span>
                                }
                                sx={{
                                    textAlign: "left",
                                    "& .MuiSelect-select": { textAlign: "left" },
                                }}
                            >
                                <MenuItem value="" disabled >
                                    ID Type
                                </MenuItem>
                                {idOptions.map((option) => (
                                    <MenuItem key={option.key} value={option.key}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>

                        </FormControl>
                        <TextField label="ID No." fullWidth margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.personalInfo.idNumber}
                            onChange={(e) => handleChange(e, "personalInfo", "idNumber")}
                            error={!!errors["personalInfo.idNumber"]}
                            helperText={errors["personalInfo.idNumber"]}
                        /> */}
                    </>
                )}

                {step === 1 && (
                    <>
                        <FormControl fullWidth margin="normal" required error={!!errors["businessInfo.entityType"]}>
                            <Select
                                value={formData.businessInfo.entityType || ""}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleChange(e, "businessInfo", "entityType");

                                    if (newValue === "Unregistered") {
                                        handleChange({ target: { value: "" } }, "businessInfo", "rcNumber");
                                        handleChange({ target: { value: "" } }, "businessInfo", "registrationYear");
                                    }
                                }}
                                displayEmpty
                                renderValue={(selected) =>
                                    selected
                                        ? idOptions.find((option) => option.key === selected)?.label
                                        : <span style={{ color: "gray" }}>Business Type</span>
                                }
                                sx={{
                                    textAlign: "left",
                                    "& .MuiSelect-select": { textAlign: "left" },
                                }}
                            >
                                <MenuItem value="" disabled>
                                    Business Type
                                </MenuItem>
                                {idOptions.map((option) => (
                                    <MenuItem key={option.key} value={option.key}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>

                            {errors["businessInfo.entityType"] && (
                                <FormHelperText>{errors["businessInfo.entityType"]}</FormHelperText>
                            )}
                        </FormControl>


                        <TextField label="Business Name" fullWidth margin="normal" required
                            placeholder="Business name or your name if not available"
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.businessInfo.entityName}
                            onChange={(e) => handleChange(e, "businessInfo", "entityName")}
                            error={!!errors["businessInfo.entityName"]}
                            helperText={errors["businessInfo.entityName"]}
                        />



                        {formData.businessInfo.entityType === "Registered" && <TextField
                            label="Year of Registration/Incorporation"
                            fullWidth
                            margin="normal"
                            required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.businessInfo.registrationYear}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "").slice(0, 4);
                                handleChange({ target: { value } }, "businessInfo", "registrationYear");
                            }}
                            inputProps={{
                                maxLength: 4,
                                pattern: "\\d{4}",
                            }}
                            error={!!errors["businessInfo.registrationYear"]}
                            helperText={errors["businessInfo.registrationYear"]}
                        />}

                        {formData.businessInfo.entityType === "Registered" && <TextField label="RC No." fullWidth margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.businessInfo.rcNumber}
                            onChange={(e) => handleChange(e, "businessInfo", "rcNumber")}
                            error={!!errors["businessInfo.rcNumber"]}
                            helperText={errors["businessInfo.rcNumber"]}
                        />}

                        <TextField label="Business Address" fullWidth multiline rows={3} margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.businessInfo.businessAddress}
                            onChange={(e) => handleChange(e, "businessInfo", "businessAddress")}
                            error={!!errors["businessInfo.businessAddress"]}
                            helperText={errors["businessInfo.businessAddress"]}
                        />
                    </>
                )}

                {step === 2 && (
                    <>
                        <TextField label="Contact Email" fullWidth margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            disabled
                            type="email"
                            value={formData.contactDetails.contactEmail}
                            onChange={(e) => handleChange(e, "contactDetails", "contactEmail")}
                            error={!!errors["contactDetails.contactEmail"]}
                            helperText={errors["contactDetails.contactEmail"]}
                        />

                        <TextField label="Business Email" fullWidth margin="normal"
                            type="email"
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}

                            value={formData.contactDetails.businessEmail}
                            onChange={(e) => handleChange(e, "contactDetails", "businessEmail")}
                            error={!!errors["contactDetails.businessEmail"]}
                            helperText={errors["contactDetails.businessEmail"]}
                        />

                        <TextField label="Mobile No." fullWidth margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.contactDetails.mobileNumber}
                            onChange={(e) => handleChange(e, "contactDetails", "mobileNumber")}
                            error={!!errors["contactDetails.mobileNumber"]}
                            helperText={errors["contactDetails.mobileNumber"]}
                        />

                        <TextField label="Social Media Handles" fullWidth margin="normal"
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.contactDetails.socialMediaHandles}
                            onChange={(e) => handleChange(e, "contactDetails", "socialMediaHandles")}
                            error={!!errors["contactDetails.socialMediaHandles"]}
                            helperText={errors["contactDetails.socialMediaHandles"]}
                        />
                    </>
                )}

                {step === 3 && (
                    <>
                        <TextField label="Bank Name" fullWidth margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.accountDetails.bankName}
                            onChange={(e) => handleChange(e, "accountDetails", "bankName")}
                            error={!!errors["accountDetails.bankName"]}
                            helperText={errors["accountDetails.bankName"]}
                        />
                        <TextField label="Account Name" fullWidth margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.accountDetails.accountName}
                            onChange={(e) => handleChange(e, "accountDetails", "accountName")}
                            error={!!errors["accountDetails.accountName"]}
                            helperText={errors["accountDetails.accountName"]}
                        />
                        <TextField label="Account Number" fullWidth margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.accountDetails.accountNumber}
                            onChange={(e) => handleChange(e, "accountDetails", "accountNumber")}
                            error={!!errors["accountDetails.accountNumber"]}
                            helperText={errors["accountDetails.accountNumber"]}
                        />
                    </>
                )}

                {step === 4 && (
                    <>
                        <>
                            <Typography variant="h6" sx={{ fontWeight: "bold", marginTop: 2 }}>
                                Authorized Representative 1
                            </Typography>
                            <TextField label="Full Name" fullWidth margin="normal" required
                                slotProps={{
                                    inputLabel: {
                                        sx: {
                                            color: "gray",
                                            "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                            "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        },
                                    },
                                }}
                                value={formData.authorizedRepresentatives.rep1.fullName}
                                onChange={(e) =>
                                    handleChange(e, "authorizedRepresentatives", "rep1", "fullName")
                                }
                                error={!!errors["authorizedRepresentatives.rep1.fullName"]}
                                helperText={errors["authorizedRepresentatives.rep1.fullName"]}
                            />
                            <TextField label="Email" fullWidth margin="normal" required
                                type="email"
                                slotProps={{
                                    inputLabel: {
                                        sx: {
                                            color: "gray",
                                            "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                            "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        },
                                    },
                                }}
                                value={formData.authorizedRepresentatives.rep1.email}
                                onChange={(e) =>
                                    handleChange(e, "authorizedRepresentatives", "rep1", "email")
                                }
                                error={!!errors["authorizedRepresentatives.rep1.email"]}
                                helperText={errors["authorizedRepresentatives.rep1.email"]}
                            />

                            <TextField label="Mobile No." fullWidth margin="normal" required
                                slotProps={{
                                    inputLabel: {
                                        sx: {
                                            color: "gray",
                                            "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                            "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        },
                                    },
                                }}

                                value={formData.authorizedRepresentatives.rep1.mobileNumber}
                                onChange={(e) =>
                                    handleChange(e, "authorizedRepresentatives", "rep1", "mobileNumber")
                                }
                                error={!!errors["authorizedRepresentatives.rep1.mobileNumber"]}
                                helperText={errors["authorizedRepresentatives.rep1.mobileNumber"]}
                            />

                            <Typography variant="h6" sx={{ fontWeight: "bold", marginTop: 3 }}>
                                Authorized Representative 2
                            </Typography>
                            <TextField label="Full Name" fullWidth margin="normal"
                                slotProps={{
                                    inputLabel: {
                                        sx: {
                                            color: "gray",
                                            "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                            "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        },
                                    },
                                }}

                                value={formData.authorizedRepresentatives.rep2.fullName}
                                onChange={(e) =>
                                    handleChange(e, "authorizedRepresentatives", "rep2", "fullName")
                                }
                                error={!!errors["authorizedRepresentatives.rep2.fullName"]}
                                helperText={errors["authorizedRepresentatives.rep2.fullName"]}
                            />
                            <TextField label="Email" fullWidth margin="normal"
                                type="email"
                                slotProps={{
                                    inputLabel: {
                                        sx: {
                                            color: "gray",
                                            "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                            "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        },
                                    },
                                }}

                                value={formData.authorizedRepresentatives.rep2.email}
                                onChange={(e) =>
                                    handleChange(e, "authorizedRepresentatives", "rep2", "email")
                                }
                                error={!!errors["authorizedRepresentatives.rep2.email"]}
                                helperText={errors["authorizedRepresentatives.rep2.email"]}

                            />
                            <TextField label="Mobile No." fullWidth margin="normal"
                                slotProps={{
                                    inputLabel: {
                                        sx: {
                                            color: "gray",
                                            "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                            "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        },
                                    },
                                }}

                                value={formData.authorizedRepresentatives.rep2.mobileNumber}
                                onChange={(e) =>
                                    handleChange(e, "authorizedRepresentatives", "rep2", "mobileNumber")
                                }
                                error={!!errors["authorizedRepresentatives.rep2.mobileNumber"]}
                                helperText={errors["authorizedRepresentatives.rep2.mobileNumber"]}
                            />
                        </>

                    </>
                )}

                {step === 5 && (
                    <>
                        <TextField label="Business Name" fullWidth margin="normal" required
                            placeholder="Business name or your name if not available"
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.acknowledgment.entityName}
                            onChange={(e) => handleChange(e, "acknowledgment", "entityName")}
                            error={!!errors["acknowledgment.entityName"]}
                            helperText={errors["acknowledgment.entityName"]}
                        />



                        <TextField label="Authorized Representative" fullWidth margin="normal" required
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={formData.acknowledgment.authorizedRep}
                            onChange={(e) => handleChange(e, "acknowledgment", "authorizedRep")}
                            error={!!errors["acknowledgment.authorizedRep"]}
                            helperText={errors["acknowledgment.authorizedRep"]}
                        />
                        <TextField
                            label="Date"
                            fullWidth
                            margin="normal"
                            type="date"
                            disabled
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        color: "gray",
                                        "&.Mui-focused": { color: "black", fontSize: "16px", fontWeight: "700" },
                                        "&.MuiInputLabel-shrink": { color: "black", fontSize: "16px", fontWeight: "700" },
                                    },
                                },
                            }}
                            value={new Date().toISOString().split("T")[0]}
                            onChange={(e) => handleChange(e, "acknowledgment", "date")}
                            error={!!errors["acknowledgment.date"]}
                            helperText={errors["acknowledgment.date"]}
                        />

                        <FormControl component="fieldset" fullWidth margin="normal">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.acknowledgment.confirmation === "yes"}
                                        onChange={(e) => handleChange(
                                            { target: { value: e.target.checked ? "yes" : "no" } },
                                            "acknowledgment",
                                            "confirmation"
                                        )}
                                        sx={{
                                            color: "gray",
                                            "&.Mui-checked": {
                                                color: "#FF9800",
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ display: "block", textAlign: "left" }}>
                                        I confirm that the provided information is correct.
                                    </Typography>
                                }
                                sx={{
                                    alignItems: "flex-start",
                                }}
                            />
                        </FormControl>
                    </>
                )}
            </Box>
        );
    };

    const validateStep = () => {
        const requiredFields = {
            0: ["firstName", "otherNames", "residentialAddress"],
            1: ["entityType", "entityName", "businessAddress"],
            2: ["contactEmail", "mobileNumber"],
            3: ["bankName", "accountName", "accountNumber"],
            4: ["rep1.fullName", "rep1.email", "rep1.mobileNumber"],
            5: ["entityName", "authorizedRep", "confirmation"]
        };

        const currentSection = [
            "personalInfo",
            "businessInfo",
            "contactDetails",
            "accountDetails",
            "authorizedRepresentatives",
            "acknowledgment"
        ][activeStep];

        let newErrors = {};
        let required = [...(requiredFields[activeStep] || [])];

        if (activeStep === 1 && formData.businessInfo?.entityType === "Registered") {
            required.push("registrationYear", "rcNumber");
        }

        required.forEach((field) => {
            const keys = field.split(".");
            let value = formData[currentSection];

            keys.forEach((key) => {
                value = value?.[key];
            });

            if (!value) {
                newErrors[`${currentSection}.${field}`] = "This field is required";
            }
            if (
                ["contactEmail", "businessEmail", "email", "rep1.email", "rep2.email"].includes(field) &&
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
            ) {
                newErrors[`${currentSection}.${field}`] = "Invalid email format";
            }

        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep01 = () => {
        const requiredFields = {
            0: ["firstName", "otherNames", "residentialAddress"],
            1: ["entityType", "entityName", "businessAddress"],
            2: ["contactEmail", "mobileNumber"],
            3: ["bankName", "accountName", "accountNumber"],
            4: ["rep1.fullName", "rep1.email", "rep1.mobileNumber"],
            5: ["entityName", "authorizedRep", "confirmation"]
        };

        const currentSection = [
            "personalInfo",
            "businessInfo",
            "contactDetails",
            "accountDetails",
            "authorizedRepresentatives",
            "acknowledgment"
        ][activeStep];

        let newErrors = { ...errors }; // Preserve existing errors
        let required = [...(requiredFields[activeStep] || [])];

        if (activeStep === 1 && formData.businessInfo?.entityType === "Registered") {
            required.push("registrationYear", "rcNumber");
        }

        required.forEach((field) => {
            const keys = field.split(".");
            let value = formData[currentSection];

            keys.forEach((key) => {
                value = value?.[key];
            });

            // Required field validation
            if (!value) {
                newErrors[`${currentSection}.${field}`] = "This field is required";
            } else {
                delete newErrors[`${currentSection}.${field}`]; // ✅ Remove the error if field is filled
            }

            // Email validation
            if (
                ["contactEmail", "businessEmail", "email", "rep1.email", "rep2.email"].includes(field)
            ) {
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    newErrors[`${currentSection}.${field}`] = "Invalid email format";
                } else {
                    delete newErrors[`${currentSection}.${field}`]; // ✅ Remove email error if valid
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClose = () => {
        setOpen(false);
        window.location.href = "https://www.booking.nimblecasa.com/mobile";
    };

    return (
        <Container maxWidth="md">
            <h3 className="kycform-title">NimbleCasa Host KYC Form</h3>

            {isSmallScreen && (
                <h6 className="kycform-sub-title"> {steps[activeStep]}</h6>
            )}

            {!isSmallScreen && (
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    sx={{
                        color: "gray",
                        "& .MuiStepIcon-root.Mui-active": {
                            color: "#ee7b0d",
                        },
                        "& .MuiStepIcon-root.Mui-completed": {
                            color: "#ee7b0d",
                        },
                    }}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

            )}

            <div className="contact-us-contact-modal step-container">
                {renderStepContent(activeStep)}
            </div>

            <Box display="flex" justifyContent="space-between" mt={2}>
                <Button disabled={activeStep === 0} onClick={handleBack} variant="contained"
                    sx={{ background: "#ee7b0d", borderRadius: "8px", textTransform: "Capitalize", px: "50px", py: "10px" }}
                >
                    Back
                </Button>
                <Button
                    onClick={activeStep === 5 ? handleSubmit : handleNext}
                    variant="contained"
                    // color="primary"
                    sx={{ background: "#ee7b0d", borderRadius: "8px", textTransform: "Capitalize", px: "50px", py: "10px" }}
                    disabled={(formData.acknowledgment.confirmation !== "yes" && activeStep === 5) || (activeStep === steps.length - 1 && activeStep !== 5) || isSubmitting}
                >
                    {isSubmitting
                        ? "Submitting"
                        : activeStep === steps.length - 1
                            ? "Submit"
                            : "Next"}
                </Button>
            </Box>
            <SuccessModal open={open} onClose={handleClose} />

        </Container>
    );
};

export default KycForm;
